import { EditorState } from "@/components/module-api-editor/types";
import { DtoSchemaWithInfo } from "@/hooks/use-dtos";
import {
  findOperationsWithInfoInDefinition,
  generateOperationIdForDefinition,
  generateResponseTitleForDefinition,
} from "@/lib/editor-mutations/oas-operations";
import { addTagToDefinition } from "@/lib/editor-mutations/oas-tags";
import {
  deref,
  findComposedKey,
  isArraySchema,
  isComposedSchema,
  isMixedSchema,
  isReference,
  RefComponents,
} from "@/lib/oas-tools/oas-tag-helpers";
import {
  OASArraySchemaObject,
  OASComponentsObject,
  OASContentObject,
  OASDefinition,
  OASNonArraySchemaObject,
  OASParameterObject,
  OASReferenceObject,
  OASResponse,
  OASSchema,
  OASSecurityRequirementDocument,
  OASSecuritySchemeObject,
  SecuritySchemeEasierType,
  supportedContentFormats,
  SupportedContentFormats,
} from "@/lib/types";
import { generatePlaceholderId, isNanoid, typedIncludes } from "@/lib/utils";
import cloneDeep from "lodash/cloneDeep";
import get from "lodash/get";
import set from "lodash/set";
import setWith from "lodash/setWith";
import unset from "lodash/unset";
import { z } from "zod";

export const simplePropertyTypes = [
  "integer",
  "number",
  "string",
  "boolean",
  "null",
] as const;
export type SimplePropertyTypes = (typeof simplePropertyTypes)[number];

export const propertyTypes = [
  ...simplePropertyTypes,
  "array",
  "object",
] as const;
export type PropertyTypes = (typeof propertyTypes)[number];

export const composedPropertyTypes = ["allOf", "anyOf", "oneOf"] as const;
export type ComposedPropertyTypes = (typeof composedPropertyTypes)[number];

export const allPropertyTypes = [
  ...propertyTypes,
  ...composedPropertyTypes,
] as const;
export type AllPropertyTypes = (typeof allPropertyTypes)[number];

// in the preview window, this propertyTypes can be clicked on
// in will expand into a larger type property
export const expandablePropertyTypes = [
  "allOf",
  "anyOf",
  "oneOf",
  "array",
  "object",
  "mixed",
];

// Normal type names are all valid values for schema.type
// readable types are meant to be displayed to the user and include values not part of the
// schema.type field like "not", "any"
export type ReadableTypeNames = AllPropertyTypes | "mixed" | "not" | "any";

export function getComponentTypeFromString(componentString: string) {
  const match = componentString.match(/#\/components\/([a-z]+)\//);
  if (!match) throw new Error("Unable to resolve component string");

  if (!match[1])
    throw new Error("Unable to resolve capturing group in component string");

  switch (match[1]) {
    case "responses":
      return "response";
    case "schemas":
      return "schema";
    case "parameters":
      return "parameter";
    default:
      throw new Error("Component string is of unknown type");
  }
}

export type ComponentId = {
  type: "schema" | "dto";
  id: string;
};

export type ComponentIdWithPath = ComponentId & { path: string };

/**
 * Moves up one level in a Lodash-like object path.
 * Args:
 * path: The object path string (e.g., "a[0].b.c").
 *
 * Returns:
 * The path moved up one level (e.g., "a[0].b").
 */
export function moveUpLevel(path: string) {
  const match = path.match(/^(.*)(\.[\w\d_%&-]+|\[\d+\])$/);
  return match ? match[1] : "";
}

export function splitObjectPath(path: string) {
  return path.split(/(\.|)/);
}

/**
 * NOTE: This will NOT remove path from required field of partent object
 */
export function removePathFromSchema(schema: OASSchema, path: string) {
  const cp = cloneDeep(schema);
  const parentPath = moveUpLevel(path);
  const parentProperty = get(cp, parentPath) as OASSchema;
  if (Array.isArray(parentProperty)) {
    const match = path.match(/.*\[(\d+)\]$/);

    if (match == null || !match[1])
      throw new Error("Could not find index to splice");
    parentProperty.splice(Number(match[1]), 1);
    set(cp, parentPath, parentProperty);
  } else {
    unset(cp, path);
  }
  return cp;
}

/**
 * Get property for given path
 */
export function getPathProperty(
  schema: OASSchema,
  path: string
): OASSchema | OASReferenceObject {
  if (path == "") return schema;
  const currentProperty = get(schema, path) as OASSchema;
  if (!currentProperty) throw new Error("No current property");
  return currentProperty;
}

function getLastPathSegment(path: string) {
  const split = path.split(".");
  return split[split.length - 1] || "";
}

export function findNearestObject(
  schema: OASSchema,
  path: string
): OASNonArraySchemaObject | undefined {
  let currentPath = path;
  while (currentPath) {
    currentPath = moveUpLevel(currentPath);
    const currentProperty = getPathProperty(schema, currentPath);
    if ("properties" in currentProperty && currentProperty.type === "object") {
      return currentProperty;
    }
  }
}

/**
 * Check if path is an object property
 *
 * - is decendant of .properties
 * - only contains valid characters
 */
function doesPathEndOnPropertyName(path: string) {
  // only properties that have a 'propertyName' at the end can be included
  // in the required array.
  // Examples:
  // a.b[0] ❌
  // a.b ✅
  return /properties\.[^.]+$/.test(path);
}

/**
 * Traverse schema tree up until I find an object with a required proeprty
 */
export function removeRequiredForProperty(schema: OASSchema, path: string) {
  const cp = cloneDeep(schema);
  // don't throw error -> function is called whenever a property is removed
  // if property inside of e.g. `allOf` the error will throw -> don't throw
  if (doesPathEndOnPropertyName(path) === false) return cp;
  const nearestObject = findNearestObject(cp, path);
  if (!nearestObject) return cp;
  if (!Array.isArray(nearestObject.required)) return cp;

  const propertyName = getLastPathSegment(path);
  nearestObject.required = nearestObject.required.filter(
    (e) => e !== propertyName
  );
  return cp;
}

export function addRequiredToProperty(rootSchema: OASSchema, path: string) {
  const cp = structuredClone(rootSchema);
  if (doesPathEndOnPropertyName(path) === false)
    throw new Error("Can only toggle required for object");

  const nearestObject = findNearestObject(cp, path);
  if (!nearestObject) throw new Error("Unable to find wrapping object");

  const requiredArr = nearestObject.required || [];

  const propertyName = getLastPathSegment(path);
  if (propertyName) {
    if (requiredArr.some((e) => e === propertyName)) {
      throw new Error("Property is already required");
    } else {
      requiredArr.push(propertyName);
    }
    nearestObject.required = requiredArr;
  }
  return cp;
}

export function changeStringPropertyMetaData({
  rootSchema,
  schema,
  path,
  propertyMetaData,
}: {
  rootSchema: OASSchema;
  schema: OASSchema;
  path: string;
  propertyMetaData: {
    // all values will be replaces
    description: string | undefined;
    default: string | undefined;
    enum: string[] | undefined;
    examples: string[] | undefined;
  };
}) {
  const cp = structuredClone(schema);
  cp.description = propertyMetaData.description;
  cp.examples = propertyMetaData.examples;
  cp.default = propertyMetaData.default;

  if (
    propertyMetaData.default != null &&
    !propertyMetaData.enum?.includes(propertyMetaData.default)
  ) {
    throw new Error("Default must be included in enum values");
  }
  cp.enum = propertyMetaData.enum;

  const newRootSchema = insertAtPath(
    rootSchema,
    path,
    // eslint-disable-next-line
    JSON.parse(JSON.stringify(cp)) // remove undefined properties from object
  );

  return newRootSchema;
}

export function changeNumberPropertyMetaData({
  rootSchema,
  schema,
  path,
  propertyMetaData,
}: {
  rootSchema: OASSchema;
  schema: OASSchema;
  path: string;
  propertyMetaData: {
    // all values will be replaces
    description: string | undefined;
    format: "float" | "double" | undefined;
    minimum: number | undefined;
    maximum: number | undefined;
    examples: number[] | undefined;
  };
}) {
  const cp = structuredClone(schema);
  cp.description = propertyMetaData.description;
  cp.minimum = propertyMetaData.minimum;
  cp.maximum = propertyMetaData.maximum;
  cp.format = propertyMetaData.format;
  cp.examples = propertyMetaData.examples;

  const newRootSchema = insertAtPath(
    rootSchema,
    path,
    // eslint-disable-next-line
    JSON.parse(JSON.stringify(cp)) // remove undefined properties from object
  );

  return newRootSchema;
}

export function changeGenericMetaData({
  rootSchema,
  schema,
  path,
  propertyMetaData,
}: {
  rootSchema: OASSchema;
  schema: OASSchema;
  path: string;
  propertyMetaData: {
    description: string | undefined;
  };
}) {
  if (isComposedSchema(schema))
    throw new Error("Metadata cannot be changes on composed types");

  const cp = structuredClone(schema);
  if (propertyMetaData.description !== undefined) {
    cp.description = propertyMetaData.description;
  }

  const newRootSchema = insertAtPath(
    rootSchema,
    path,
    // eslint-disable-next-line
    JSON.parse(JSON.stringify(cp)) // remove undefined properties from object
  );

  return newRootSchema;
}

export function changeBooleanMetaData({
  rootSchema,
  schema,
  path,
  propertyMetaData,
}: {
  rootSchema: OASSchema;
  schema: OASSchema;
  path: string;
  propertyMetaData: {
    description: string | undefined;
    examples: boolean[] | undefined;
  };
}) {
  if (isComposedSchema(schema))
    throw new Error("Metadata cannot be changes on composed types");

  const cp = structuredClone(schema);
  if (propertyMetaData.description !== undefined) {
    cp.description = propertyMetaData.description;
    cp.examples = propertyMetaData.examples;
  }

  const newRootSchema = insertAtPath(
    rootSchema,
    path,
    // eslint-disable-next-line
    JSON.parse(JSON.stringify(cp)) // remove undefined properties from object
  );

  return newRootSchema;
}

export function changeReferenceMetaData({
  rootSchema,
  schema,
  path,
  propertyMetaData,
}: {
  rootSchema: OASSchema;
  schema: OASSchema;
  path: string;
  propertyMetaData: {
    description: string | undefined;
  };
}) {
  if (!isReference(schema))
    throw new Error("This metadata can only change reference types");

  const cp = structuredClone(schema);
  cp.description = propertyMetaData.description;

  const newRootSchema = insertAtPath(
    rootSchema,
    path,
    // eslint-disable-next-line
    JSON.parse(JSON.stringify(cp)) // remove undefined properties from object
  );

  return newRootSchema;
}

export function changeIntegerPropertyMetaData({
  rootSchema,
  schema,
  path,
  propertyMetaData,
}: {
  rootSchema: OASSchema;
  schema: OASSchema;
  path: string;
  propertyMetaData: {
    // all values will be replaces
    description: string | undefined;
    format: "int32" | "int64" | undefined;
    minimum: number | undefined;
    maximum: number | undefined;
    examples: number[] | undefined;
  };
}) {
  const cp = structuredClone(schema);
  cp.description = propertyMetaData.description;
  cp.minimum = propertyMetaData.minimum;
  cp.maximum = propertyMetaData.maximum;
  cp.format = propertyMetaData.format;
  cp.examples = propertyMetaData.examples;

  const newRootSchema = insertAtPath(
    rootSchema,
    path,
    // eslint-disable-next-line
    JSON.parse(JSON.stringify(cp)) // remove undefined properties from object
  );

  return newRootSchema;
}

export function insertAtPath<T extends object = OASSchema>(
  schema: T,
  path: string,
  newSchema: OASSchema | OASReferenceObject
): T {
  if (path === "") return cloneDeep(newSchema as T);
  const cp = cloneDeep(schema);
  set(cp, path, newSchema);
  return cp;
}

export function getPristineContentObject(): OASContentObject {
  return {
    "application/json": {
      schema: getPristineType("object"),
    },
  };
}

export function getPristineType(
  type: OASSchema["type"] | ComposedPropertyTypes
): OASSchema {
  switch (type) {
    case "boolean":
    case "integer":
    case "number":
    case "string":
      return { type: type };
    case "array":
      return {
        type: "array",
        items: {
          type: "string",
        },
      };
    case "object":
      return {
        type: "object",
      };
    case "null":
      return {
        type: "null",
      };
    case "oneOf":
      return {
        oneOf: [{ type: "object", properties: {} }],
      };
    case "anyOf":
      return {
        anyOf: [{ type: "object", properties: {} }],
      };
    case "allOf":
      return {
        allOf: [{ type: "object", properties: {} }],
      };
    default:
      throw new Error("Trying to change to unknown type");
  }
}

function isReferenceObject(
  schema: OASSchema | OASReferenceObject
): schema is OASReferenceObject {
  if ("$ref" in schema) return true;
  return false;
}

export function changeType(
  schema: OASSchema,
  path: string,
  newType: AllPropertyTypes
) {
  const placeholderSchema =
    getPristineType(newType) || getPristineType("object");
  if (path === "") return placeholderSchema;
  return insertAtPath(schema, path, placeholderSchema);
}

export function changeTypeToModel(
  schema: OASSchema,
  path: string,
  componentId: ComponentId
) {
  return insertAtPath(schema, path, {
    $ref: `#/components/schemas/${componentId.id}`,
  });
}

export function getFirstSchemaFromContentObject(obj: OASContentObject) {
  const keys = Object.keys(obj);
  if (!keys.length) return undefined;
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    if (typedIncludes(supportedContentFormats, key) && obj[key].schema)
      return obj[key].schema;
  }
}

export function getFirstConentTypeFromContentObject(
  obj: OASContentObject
): SupportedContentFormats | undefined {
  const keys = Object.keys(obj);
  if (!keys.length) return undefined;
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    if (typedIncludes(supportedContentFormats, key)) return key;
  }
}

export function getDeepSchemaOrError(obj: OASContentObject | undefined) {
  if (!obj) return getPristineType("null");
  const schema = getFirstSchemaFromContentObject(obj);
  if (!schema) return getPristineType("null");
  return schema;
}

function isDirectAncestorOfComposedProperty(
  rootSchema: OASSchema,
  path: string
) {
  const parentPath = moveUpLevel(path);
  const parentProperty = get(rootSchema, parentPath) as OASSchema;
  return Array.isArray(parentProperty);
}

function getAllDtosFromComponentsObject(
  componentsObject: OASComponentsObject
): DtoSchemaWithInfo[] {
  return Object.entries(componentsObject.schemas || {})
    .filter(([, schema]) => {
      return schema["x-fiddle-dto-info"];
    })
    .map(([name, schema]) => ({ dtoSchema: schema, name }));
}

type SchemaWithInfo = {
  schema: OASSchema;
  name: string;
};

function getAllSchemasFromComponentsObject(
  componentsObject: OASComponentsObject
): SchemaWithInfo[] {
  return Object.entries(componentsObject.schemas || {})
    .filter(([, schema]) => {
      return !schema["x-fiddle-dto-info"];
    })
    .map(([name, schema]) => ({ schema: schema, name }));
}

type BaseTypeOptions = {
  propertyTypes: readonly AllPropertyTypes[];
  schemas: readonly SchemaWithInfo[];
  dtos: readonly DtoSchemaWithInfo[];
};

export type TypeOptions = ReturnType<typeof getTypeOptions>;
/**
 * Rules for available types.
 *
 * TOP LEVEL REFERENCES
 * dtos + schemas: Schemas and DTOs cannot have top-level references
 *
 * DIRECT ANCESTOR OF COMPOSED TYPE
 * schemaTypes: cannot be composes types as well: allof.allof is forbidden
 */
export function getTypeOptions({
  componentsObject,
  schema,
  rootSchema,
  path,
  allowTopLevelReferences,
}: {
  rootSchema: OASSchema;
  schema: OASSchema;
  componentsObject: OASComponentsObject;
  path: string;
  allowTopLevelReferences: boolean;
}): BaseTypeOptions & { innerArrayTypeOptions: BaseTypeOptions | undefined } {
  const allDtos = getAllDtosFromComponentsObject(componentsObject);
  const allSchemas = getAllSchemasFromComponentsObject(componentsObject);

  let dtos: Array<DtoSchemaWithInfo> = [...allDtos];
  let schemas: Array<SchemaWithInfo> = [...allSchemas];
  let propertyTypes: Array<AllPropertyTypes> = [...allPropertyTypes];

  // flags for modification
  const isComposed = isDirectAncestorOfComposedProperty(rootSchema, path);
  const isArraySchema = schema.type === "array";

  // filter
  if (isComposed) {
    propertyTypes = [...propertyTypes];
  }
  if (!allowTopLevelReferences && path === "") {
    dtos = [];
    schemas = [];
  }

  const innerArrayTypeOptions = isArraySchema
    ? {
        dtos: allDtos,
        schemas: allSchemas,
        propertyTypes: [...allPropertyTypes],
      }
    : undefined;
  return {
    propertyTypes,
    dtos,
    schemas,
    innerArrayTypeOptions,
  };
}

export function isNonArraySchemaObject(
  schema: OASSchema
): schema is OASNonArraySchemaObject {
  if (
    schema.type === "array" ||
    Array.isArray(schema.type) ||
    isReferenceObject(schema)
  )
    return false;
  return true;
}

export function addPlaceholderToPath(
  rootSchema: OASSchema,
  path: string
): [schema: OASSchema, placeholderId: string | undefined] {
  const property = getPathProperty(rootSchema, path);
  const cp = cloneDeep(property);

  if (Array.isArray(cp)) throw new Error("Can't add placeholder to array");
  if ("$ref" in cp) throw new Error("Can't add placeholder to ref schema");

  if ((cp.oneOf || cp.allOf || cp.anyOf) && isNonArraySchemaObject(cp)) {
    const composedKey = findComposedKey(cp);
    cp[composedKey]?.push({
      type: "object",
      properties: {},
    });
    const newSchema = insertAtPath(rootSchema, path, cp);
    return [newSchema, undefined];
  }
  if ("type" in cp && cp.type === "object") {
    const placeholderId = generatePlaceholderId();
    if (!cp.properties) cp.properties = {};
    const newSchema = insertAtPath(
      rootSchema,
      `${path}${path === "" ? "properties" : ".properties"}`,
      {
        ...cp.properties,
        [placeholderId]: { type: "string" },
      }
    );
    return [newSchema, placeholderId];
  }
  throw new Error("Can't add placeholder to invalid type");
}

export function changePropertyName(
  schema: OASSchema,
  path: string,
  newPropertyName: string
) {
  if (doesPathEndOnPropertyName(path) === false)
    throw new Error("Path must end on property name");

  let cp = structuredClone(schema);

  const oldPropertyName = getLastPathSegment(path);
  const parentPath = moveUpLevel(path);
  const nearestObject = findNearestObject(cp, path);

  const newPath = `${parentPath}.${newPropertyName}`;

  const property = getPathProperty(cp, path);

  const isRequired = (nearestObject?.required || []).some(
    (e) => e === oldPropertyName
  );

  if (isRequired) {
    cp = removeRequiredForProperty(cp, path);
  }
  // no matter if the name has changed or not, we remove
  unset(cp, path);

  setWith(cp, newPath, property, Object);

  if (isRequired) {
    cp = addRequiredToProperty(cp, newPath);
  }

  return cp;
}

/**
 * This is a precaution. A lot of the editor depends on operationIds being present.
 * So far, no state was introduced where the operationId was missing but this can
 * prevent future errors.
 */
function healMissingOperationId(editorState: EditorState): EditorState {
  const operationsWithInfo = findOperationsWithInfoInDefinition(
    editorState.data
  );
  operationsWithInfo.forEach((operationWithInfo) => {
    const { operation } = operationWithInfo;
    if (!operation.operationId)
      operation.operationId = generateOperationIdForDefinition(
        operationWithInfo,
        editorState.data
      );
  });
  return editorState;
}

/**
 * 1 Heal objects that have both .items and .properties
 * 1.1. If type "array" remove .properties
 * 1.2 If type "object" remove .items
 * 2 Heal arrays that dont have array.items defined
 */
export function healArraysWithProperties(editorState: EditorState) {
  const oas = editorState.data;

  function traverse(oas: any) {
    if (typeof oas !== "object" || oas === null) {
      return;
    }
    if (Array.isArray(oas)) {
      oas.forEach((e) => {
        traverse(e);
      });
      return;
    }
    if (
      typeof oas === "object" &&
      oas !== null &&
      "type" in oas &&
      // eslint-disable-next-line
      oas.type === "array"
    ) {
      if ("properties" in oas) {
        // eslint-disable-next-line
        delete oas.properties;
      }
      if (!("items" in oas)) {
        // eslint-disable-next-line
        (oas as any).items = {
          type: "object",
        };
      }
    } else if (
      typeof oas === "object" &&
      oas !== null &&
      "type" in oas &&
      // eslint-disable-next-line
      oas.type === "object" &&
      "items" in oas
    ) {
      // eslint-disable-next-line
      delete oas.items;
    }
    // eslint-disable-next-line
    for (const [, value] of Object.entries(oas)) {
      traverse(value);
    }
  }
  traverse(oas);
  return editorState;
}

export function healNonSensicalOperationIds(editorState: EditorState) {
  const definition = editorState.data;
  const operations = findOperationsWithInfoInDefinition(definition);
  operations.forEach((op) => {
    if (!op.operation.operationId) {
      op.operation.operationId = generateOperationIdForDefinition(
        op,
        definition
      );
    } else {
      const operationId = op.operation.operationId;
      if (isNanoid(operationId) || /[0-9]/.test(operationId[0])) {
        op.operation.operationId = generateOperationIdForDefinition(
          op,
          definition
        );
      }
    }
  });
  return editorState;
}

/**
 * It's possible that operations use tags that are not present in the top-level
 * tag array. This function synchonizes that top-level tag array with the tags
 * used in operations.
 */
export function healMissingTags(editorState: EditorState): EditorState {
  let definition = editorState.data;
  const operations = findOperationsWithInfoInDefinition(definition);

  const usedTags: string[] = [];

  operations.forEach((op) => {
    if (op.operation.tags?.length) {
      op.operation.tags.forEach((tag) => usedTags.push(tag));
    }
  });

  function doesTagExistInDefinition(
    tagName: string,
    definition: OASDefinition
  ) {
    return definition.tags?.some((e) => e.name === tagName);
  }

  usedTags.forEach((tagName) => {
    if (!doesTagExistInDefinition(tagName, definition)) {
      definition = addTagToDefinition({
        definition,
        tagObject: { name: tagName },
      });
    }
  });

  return { document_type: "oas_api_3_1", data: definition };
}

/**
 * I'm unsure why we need the response.titles
 */
export function healMissingResponseTitles(editorState: EditorState) {
  const definition = editorState.data;
  const operations = findOperationsWithInfoInDefinition(definition);

  operations.forEach((op) => {
    for (const [responseCodeStr, responseWrapper] of Object.entries(
      op.operation.responses || {}
    )) {
      if ("$ref" in responseWrapper) return;
      for (const [_, response] of Object.entries(
        responseWrapper.content || {}
      )) {
        if (!response.schema || "$ref" in response.schema) return;
        if (!response.schema?.title) {
          set(
            response,
            "schema.title",
            generateResponseTitleForDefinition(
              responseCodeStr,
              op.operation.operationId || "NoOperationId"
            )
          );
        }
      }
    }
  });

  return editorState;
}

export function healCommonEditorErrors(editorState: EditorState) {
  return healArraysWithProperties(editorState);
}

export function validateEditorStateBeforeUse(editorState: EditorState) {
  return healMissingResponseTitles(
    healMissingTags(
      healNonSensicalOperationIds(healMissingOperationId(editorState))
    )
  );
}

export function getSchemaTypeName(schema: OASSchema): ReadableTypeNames {
  if (schema.allOf) return "allOf";
  if (schema.anyOf) return "anyOf";
  if (schema.oneOf) return "oneOf";
  if (schema.not) return "not"; // Optional case for `not`

  // If the `type` is missing, infer from the presence of `properties` or default to "any"
  if (!schema.type) {
    if (schema.properties) return "object";
    return "any";
  }

  if (Array.isArray(schema.type)) return "mixed"; // Safeguard for JSON Schema type arrays
  return schema.type; // Return the type directly

  // No need for an extra check of `!schema.type` since we've already handled it
}

export function getResponseTypeName(response: OASResponse) {
  if (isReference(response)) return deref(response.$ref);
  if (!response.content) return "Unknown";
  const schema = getFirstSchemaFromContentObject(response.content);
  return schema ? getSchemaTypeName(schema) : "Unknown";
}

export function getComponentParameterTypeName(parameter: OASParameterObject) {
  const schema = parameter.schema;
  if (!schema) return "Unknown";
  if (isReference(schema)) return deref(schema.$ref);
  return getSchemaTypeName(schema);
}

export function getComponentSchemaFromComponentsObject(
  componentsObject: OASComponentsObject | undefined,
  componentId: ComponentId
): OASSchema | undefined {
  if (!componentsObject)
    throw new Error(`This workspace has no components object`);
  return componentsObject["schemas"]?.[componentId.id];
}

export function getComponentSchemaFromComponentsObjectOrError(
  componentsObject: OASComponentsObject | undefined,
  componentId: ComponentId,
  format: SupportedContentFormats = "application/json"
): OASSchema {
  if (!componentsObject)
    throw new Error(`This workspace has no components object`);
  if (componentId.type === "schema") {
    const value = componentsObject["schemas"]?.[componentId.id];
    if (!value)
      throw new Error(`Unable fo find component with name: ${componentId.id}`);
    return value;
  }
  if (componentId.type === "dto") {
    const schema = componentsObject["schemas"]?.[componentId.id];
    if (!schema)
      throw new Error(`Unable to find dto with id: ${componentId.id}`);
    if (!schema)
      throw new Error(
        `Dto with id ${componentId.id} does not have a shema in the format of: ${format}`
      );
    return schema;
  }

  const _never: never = componentId.type;
  throw new Error(`Unknown componentId type: ${_never}`);
}

export function getComponentNameFromOASRefObject(
  referenceObject: OASReferenceObject
) {
  return referenceObject.$ref.replace(/^#\/components\/schemas\//, "");
}

export const zodSchemaSchema = z
  .record(z.string(), z.any())
  .transform((schema) => cleanSchema(schema)) as z.ZodType<Record<string, any>>;

export const zodContentObject = z.record(
  z.enum(supportedContentFormats),
  z.object({
    schema: zodSchemaSchema.optional(),
    examples: z.any().optional(),
  })
);

export const supportedQueryParameterFormats = [
  "form",
  "spaceDelimited",
  "pipeDelimited",
  "deepObject",
] as const;
export const supportedPathParameterFormats = [
  "simple",
  "label",
  "matrix",
] as const;
export const supportedCookieParameterFormats = ["form"] as const;
export const supportedHeaderParameterFormats = ["simple"] as const;

export function wrapSchemaInArray(schema: OASNonArraySchemaObject): OASSchema {
  return {
    type: "array",
    items: schema,
  };
}

export function unwrapSchemaFromArray(schema: OASArraySchemaObject) {
  return schema.items;
}

export function cleanSchema(schema: OASSchema): OASSchema {
  function traverse<T>(obj: T): T {
    if (obj === null || obj === undefined) return obj;

    if (typeof obj !== "object") return obj;

    if (Array.isArray(obj)) {
      return obj.map(traverse) as unknown as T;
    }

    if (obj instanceof Date) return obj;

    const result = {} as T;
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        if (!key.startsWith("%%%")) {
          result[key] = traverse(obj[key]);
        }
      }
    }

    return result;
  }

  return traverse(schema);
}

export function convertToReadableContentFormat(e: SupportedContentFormats) {
  const found = e.replace(/.*\/([\w.+-]+)$/, "$1");
  return found.length ? found : e;
}

export const getSecurityRequirementObjectValues = (
  secObj: OASSecurityRequirementDocument
) => {
  const entries = Object.entries(secObj);
  if (!entries.length)
    throw new Error("Not Allowed: Cannot handle empty security object here.");
  if (entries.length > 1)
    throw new Error(
      "Security object with more than one authorization object are not supported."
    );

  const [schemeName, scopes] = entries[0];
  if (!schemeName)
    throw new Error("SecurityRequirementObject: SchemeName is not defined");
  if (!scopes)
    throw new Error("SecurityRequirementObject: Scopes are not defined");

  return { schemeName, scopes };
};

export const securitySchemeTemplateMap: Record<
  SecuritySchemeEasierType,
  OASSecuritySchemeObject
> = {
  ApiKey: {
    type: "apiKey",
    in: "header",
    name: "API_KEY",
    description:
      "Key Auth: Requests contain a valid `API_KEY` in the request headers.",
  },
  BearerBasic: {
    type: "http",
    scheme: "Basic",
    bearerFormat: "Basic Access Authentication",
    description:
      "Basic Auth: Authenticated requests contain a valid `Authorization: Basic base64(username:password)` header",
  },
  BearerJwt: {
    type: "http",
    scheme: "Bearer",
    bearerFormat: "Json Web Token (JWT)",
    description:
      "Jwt Auth: Authenticated requests contain a valid Json Web Token (JWT) as part of the `Authorization: Bearer <JWT>` header.",
  },
  BearerCustom: {
    type: "http",
    scheme: "Bearer",
    bearerFormat: "Custom token",
    description:
      "Authorization: Authenticated requests contain a valid 'Authorization's header.",
  },
};

/**
 * We have a $ref string and want to find out if it points to a response, parameter, etc.
 */
export function getRefInfo($ref: string) {
  const regex = /^#\/components\/([^/]+)\/([^/]+)\/?$/;
  const matches = $ref.match(regex);

  if (matches?.length !== 3)
    throw new Error(`Misconfigured: Invalid reference at: ${$ref}`);
  const refComponent = matches[1] as RefComponents;
  const refName = matches[2];

  return { refComponent, refName };
}

export function resolveReferenceSchema(
  schema: OASSchema,
  path: string,
  componentsObject: OASComponentsObject
) {
  const cp = structuredClone(schema);
  const property = getPathProperty(cp, path);

  if (!isReference(property))
    throw new Error("Internal: Must be reference schema");

  const { refComponent, refName } = getRefInfo(property.$ref);

  if (refComponent !== "schemas")
    throw new Error(
      `Misconfiguration: Reference points to invalid component: ${refComponent}`
    );

  const refSchema = componentsObject.schemas?.[refName];

  if (!refSchema)
    throw new Error(
      `Misconfiguration: Unable to find valid schema for schema $ref: ${refName}`
    );

  const newSchema = insertAtPath(cp, path, refSchema);
  return newSchema;
}

/**
 * Find property in path and wrap it inise an array
 */
export function wrapPathSchemaInArray(schema: OASSchema, path: string) {
  const cp = structuredClone(schema);
  const property = getPathProperty(cp, path);

  if (isArraySchema(property) || isMixedSchema(property)) {
    throw new Error(
      `Misconfigued: Unable to wrap schema in array: ${JSON.stringify(property)}`
    );
  }

  const newProperty = wrapSchemaInArray(property);

  const newSchema = insertAtPath(cp, path, newProperty);

  return newSchema;
}

/**
 * Find property in path and unwrap outer array
 */
export function unwrapPathSchemaFromArray(schema: OASSchema, path: string) {
  const cp = structuredClone(schema);
  const property = getPathProperty(cp, path);

  if (!isArraySchema(property)) {
    throw new Error(
      `Misconfigued: Unable to unwrap schema. Schema is not of type array: ${JSON.stringify(property)}`
    );
  }

  const newProperty = unwrapSchemaFromArray(property);
  const newSchema = insertAtPath(cp, path, newProperty);
  return newSchema;
}
